import React, {useEffect, useState, useRef, useCallback} from "react";
import {useHistory, useLocation, useParams} from "react-router-dom";
import useAxios from "../../../utils/useAxios";
import {useTranslation} from "react-i18next";
import Skeleton from "react-loading-skeleton";
import {CustomModal} from "../../../components/CustomModel/CustomModal";
import {getOptionName} from "../../../utils/getOptionName";
import {COUNTRY_OPTIONS, handleItemClick, LEVEL_OPTIONS} from "../../../constants";
import TournamentPlayerForm from "./TournamentPlayerForm";
import ParticipantActionsModal from "../../../components/ParticipantActionsModal";
import FeatherIcon from "feather-icons-react";
import PlayerForm from "../../players/PlayerForm";
import {SmoothAvatar} from "../../../components/SmoothAvatar";
import {toast} from "react-toastify";
import useViewMode from "../../../hooks/useViewMode";
import PlayersGridView from "./PlayersGridView";
import IconDropdown from "../../../components/IconDropdown";
import PageLoader from "../../../components/PageLoader";
import EmptyData from "../../EmptyData";
import { openTournamentSettings } from "../../../utils/tournamentSettingsBus";
import { openTournamentRating } from "../../../utils/tournamentRatingBus";
import useTournamentSocket from "../../../hooks/useTournamentSocket";
import { useTheme } from "../../../contexts/ThemeContext";
import { CompactTableRow } from "../../../components/CompactTableRow";
import { chipsMatchEntity, searchStateClass } from "../../../utils/searchMatch";
import { publishTournamentSearchOptions } from "../../../utils/tournamentSearchOptionsBus";
import { cacheBustUrl } from "../../../utils/cacheBustUrl";
import { invalidateTournamentPlayerImage } from "../../../hooks/useTournamentPlayerImages";


// Fields PlayerForm can actually change - the ONLY ones copied back onto a row
// after an edit. A blanket {...row, ...updatedPlayer} looked simpler but is
// wrong here: a row is a tournament-participation object whose `id` is the
// participation id, while `updatedPlayer.id` is the PLAYER id (that's exactly
// why every call site reads `player_id || id`). Spreading the whole response
// silently overwrote the row's own `id`, and the fetchPlayers() that used to
// follow was papering over it by throwing the merged state away again.
const PLAYER_PROFILE_FIELDS = [
    "first_name", "last_name", "email", "phone", "gender", "birthday",
    "level", "country", "city", "resident", "info", "image", "width", "height",
];

const mergePlayerProfile = (row, updatedPlayer) => {
    const merged = { ...row };
    PLAYER_PROFILE_FIELDS.forEach(key => {
        if (key in updatedPlayer) merged[key] = updatedPlayer[key];
    });
    // A replaced photo keeps its filename, and /media/ is cached for 30 days -
    // without this the row would keep showing the OLD avatar even though the
    // save succeeded and the server already has the new one.
    if (merged.image) merged.image = cacheBustUrl(merged.image);
    return merged;
};


const TournamentPlayers = ({tournament: initialTournament, loading, is_authorized, searchChips = []}) => {
    const {id} = useParams();
    const api = useAxios();
    const {t} = useTranslation();

    let history = useHistory();
    const location = useLocation();

    const [players, setPlayers] = useState([]);
    // true from the start (not false) - `ordering` below is still null on
    // the very first render (it's only set by an effect, after
    // initialTournament resolves), so without this the empty-placeholder
    // slot cards briefly render first (players=[] but tournament.
    // player_quantity already known) before the fetchPlayers effect even
    // fires and flips this to true - a slow connection turns that into a
    // visible blocks-then-spinner-then-blocks flicker. fetchPlayers' own
    // finally already flips this back to false once real data (or a
    // failure) arrives, same as before.
    const [playersLoading, setPlayersLoading] = useState(true);
    const [playerModalOpen, setPlayerModalOpen] = useState(false);
    const [editingSlot, setEditingSlot] = useState(null);
    const [tournament, setTournament] = useState(initialTournament);
    const [ordering, setOrdering] = useState(null);
    const [editingPlayerId, setEditingPlayerId] = useState(null);
    const [selectedSorting, setSelectedSorting] = useState(null);
    const [teamNameDraft, setTeamNameDraft] = useState({});
    // Scrolls the first matched card/row into view once per distinct match -
    // see the effect near the bottom of this component. Tracking the actual
    // DOM node (not just a boolean) means a *different* match scrolling into
    // view (more results loaded, or the query changed) re-scrolls, but
    // re-renders that land on the same match again (a sibling card
    // re-rendering, a keystroke elsewhere) don't fight the user's own
    // scrolling.
    const lastScrolledMatchRef = useRef(null);
    const { viewMode, toggleViewMode } = useViewMode("players_view_mode", "grid");
    // Compact Table is a user-facing-only density preference - an admin
    // editing this tournament still gets the regular view regardless of the
    // stored setting, only seeing it once they switch to Public View for
    // this tournament. See Header.js/BottomNav.js for the matching
    // toggle-visibility gate.
    const { compactTable: rawCompactTable } = useTheme();
    const compactTable = rawCompactTable && !is_authorized;
    // Compact Table Mode has no grid counterpart (never did - Phase 1
    // deliberately left PlayersGridView untouched) - forcing List here
    // instead of just leaving a stale "grid" preference on screen is what
    // makes turning Compact table on actually change something the moment
    // you're looking at this tab, rather than only on the next manual
    // List/Grid tap. The stored `players_view_mode` preference itself is
    // untouched, so switching Compact table back off returns to whichever
    // view was picked before.
    const effectiveViewMode = compactTable ? "list" : viewMode;

    const [editProfileOpen, setEditProfileOpen] = useState(false);
    const [editingPlayer, setEditingPlayer] = useState(null);
    const [actionsModal, setActionsModal] = useState(null);

    const [nextUrl, setNextUrl] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const loadingRef = useRef(false);
    // /teams/ nests each member's own `rating` at 0 for every player on a
    // grade>1 tournament (backend bug, see backend-tasks/2026-10-20-teams-
    // players-rating-field-zero.md and the earlier 2026-09-12 one) while the
    // flat /players/ endpoint has that same player's real rating straight
    // away - fetchPlayers cross-references it in (same fix
    // TournamentRatingPlayers.js's own team branch now applies), and stashes
    // the map here so the infinite-scroll append paths below can reuse it
    // without a second round-trip per page.
    const ratingMapRef = useRef(new Map());
    const enrichTeamsWithRating = (list) => {
        if (tournament.grade <= 1 || ratingMapRef.current.size === 0) return list;
        return list.map(team => team ? {
            ...team,
            players: (team.players || []).map(p => p ? { ...p, rating: ratingMapRef.current.get(String(p.player_id)) ?? p.rating } : p)
        } : team);
    };

    useTournamentSocket(tournament?.id, (payload) => {
        if (payload?.wake_up || payload?.event === "players_updated") {
            fetchPlayers();
        }
    });

    useEffect(() => {
        if (initialTournament) {
            setTournament(initialTournament);
            if (!ordering) {
                setOrdering(initialTournament.grade > 1 ? "-general-rating" : "-rating");
            }
        }
    }, [initialTournament]);

    useEffect(() => {
        if (!id || !tournament || !ordering) return;
        fetchPlayers(ordering);
    }, [tournament, ordering]);

    useEffect(() => {
        const timers = Object.entries(teamNameDraft).map(([teamId, name]) => {
            const timeout = setTimeout(async () => {
                try {
                    await api.patch(`/api/v1.0/tournaments/teams/${teamId}/`, { name });
                    setPlayers(prev =>
                        prev.map(team =>
                            team?.id === teamId ? { ...team, name } : team
                        )
                    );
                } catch (e) {
                    console.error(e);
                }
            }, 500);

            return timeout;
        });

        return () => timers.forEach(clearTimeout);
    }, [teamNameDraft]);

    // true/false once a chip is active, null while there are none - a team
    // matches on its own name OR any of its players', same rule
    // Bracket.js/TournamentGroupTable.js/TournamentRatingPlayers.js use.
    const matchOf = useCallback((teamOrPlayer) => {
        if (!teamOrPlayer) return null;
        return chipsMatchEntity(searchChips, {
            names: tournament?.grade > 1 ? [teamOrPlayer.name, ...(teamOrPlayer.players || [])] : [teamOrPlayer]
        });
    }, [tournament?.grade, searchChips]);

    // Matching only happens against what's already loaded (this endpoint
    // has no server-side search param), so while typing keeps pulling
    // further infinite-scroll pages until a match turns up or pagination
    // runs out - otherwise a player past the first page would never get
    // the chance to highlight at all.
    useEffect(() => {
        if (searchChips.length > 0 && nextUrl && !loadingRef.current) {
            const isFound = players.some(teamOrPlayer => matchOf(teamOrPlayer) === true);
            if (!isFound) {
                loadingRef.current = true;
                setLoadingMore(true);

                api.get(nextUrl).then(res => {
                    setPlayers(prev => [...prev, ...enrichTeamsWithRating(res.data.results || [])]);
                    setNextUrl(res.data.next ? res.data.next.replace(/^https?:\/\/[^/]+/, '') : null);
                }).catch(e => {
                    console.error(e);
                }).finally(() => {
                    loadingRef.current = false;
                    setLoadingMore(false);
                });
            }
        }
    }, [searchChips, players, nextUrl, matchOf]);

    // Player-name chip options for the global search box (Tournament.js) -
    // it owns the search UI but not this tab's data, see
    // tournamentSearchOptionsBus's own comment. A dedicated fetch, not built
    // off the `players` state above - that one only holds whichever page(s)
    // infinite scroll has loaded so far, so a participant seated further
    // down the roster wouldn't offer a suggestion while typing until you'd
    // already scrolled to them. Розклад's own search doesn't have this
    // problem (a single stage's schedule is never paginated to begin with),
    // so this pulls the whole roster in one shot, capped at player_quantity
    // (the roster can never exceed its own slot count) - independent of,
    // and without disturbing, the paginated list above.
    useEffect(() => {
        if (!id || !tournament?.player_quantity) return;
        let cancelled = false;
        const url = tournament.grade > 1
            ? `/api/v1.0/tournaments/${id}/teams/?limit=${tournament.player_quantity}`
            : `/api/v1.0/tournaments/${id}/players/?limit=${tournament.player_quantity}`;
        api.get(url).then(res => {
            if (cancelled) return;
            const all = res.data.results || res.data || [];
            const names = new Map();
            const addName = (p) => {
                if (p?.last_name) {
                    const full = `${p.last_name} ${p.first_name || ''}`.trim();
                    names.set(full, full);
                }
            };
            if (tournament.grade > 1) {
                all.forEach(team => {
                    if (!team) return;
                    if (team.name) names.set(team.name, team.name);
                    (team.players || []).forEach(addName);
                });
            } else {
                all.forEach(addName);
            }
            publishTournamentSearchOptions(
                names.size > 0
                    ? [{ label: t("Players"), options: Array.from(names.values()).map(n => ({ value: `p_${n}`, label: n, type: 'player' })) }]
                    : []
            );
        }).catch(e => console.error(e));
        return () => { cancelled = true; };
    }, [id, tournament?.grade, tournament?.player_quantity, ordering, t]);

    // Scrolls the first highlighted card/row into view - queried off the DOM
    // (searchStateClass's own ".search-match", set on whichever card/row
    // just matched above) rather than tracked through the render functions'
    // own props, since there are 4 of them (card/team/compact variants) and
    // this only needs to run once per distinct match anyway.
    useEffect(() => {
        if (searchChips.length === 0) {
            lastScrolledMatchRef.current = null;
            return;
        }
        const match = document.querySelector("#participants-block .search-match");
        if (match && match !== lastScrolledMatchRef.current) {
            lastScrolledMatchRef.current = match;
            match.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [searchChips, players]);

    const fetchPlayers = async (order = ordering) => {
        setPlayersLoading(true);
        loadingRef.current = true;
        try {
            const isExport = new URLSearchParams(location.search).get("export") === "true";
            const isTeam = tournament.grade > 1;
            let url = isTeam
                ? `/api/v1.0/tournaments/${id}/teams/?ordering=${order}`
                : `/api/v1.0/tournaments/${id}/players/?ordering=${order}`;
            if (isExport) {
                url += `&limit=100`;
            }

            // Fired alongside the teams/ request (not awaited after it) - see
            // enrichTeamsWithRating above for why this second call exists:
            // /teams/ itself always returns rating:0 for every member.
            const [res, flatRes] = await Promise.all([
                api.get(url),
                isTeam
                    ? api.get(`/api/v1.0/tournaments/${id}/players/?limit=${(tournament.player_quantity || 0) * (tournament.grade === 2 ? 2 : (tournament.team_size || 4)) || 500}`).catch(e => { console.error(e); return null; })
                    : Promise.resolve(null)
            ]);

            if (isTeam && flatRes) {
                ratingMapRef.current = new Map(
                    (flatRes.data.results || flatRes.data || [])
                        .filter(Boolean)
                        .map(p => [String(p.player_id), p.rating])
                );
            }

            setPlayers(enrichTeamsWithRating(res.data.results || res.data || []));
            setNextUrl(res.data.next ? res.data.next.replace(/^https?:\/\/[^/]+/, '') : null);
        } catch (e) {
            console.error(e);
        } finally {
            setPlayersLoading(false);
            loadingRef.current = false;
        }
    };

    const observer = useRef();
    const lastElementRef = useCallback(node => {
        if (loadingRef.current) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && !!nextUrl && !loadingRef.current) {
                loadingRef.current = true;
                setLoadingMore(true);

                api.get(nextUrl).then(res => {
                    setPlayers(prev => [...prev, ...enrichTeamsWithRating(res.data.results || [])]);
                    setNextUrl(res.data.next ? res.data.next.replace(/^https?:\/\/[^/]+/, '') : null);
                }).catch(e => {
                    console.error(e);
                }).finally(() => {
                    loadingRef.current = false;
                    setLoadingMore(false);
                });
            }
        }, { rootMargin: "100px" });

        if (node) observer.current.observe(node);
    }, [nextUrl]);

    const handlePlayerAdded = (newPlayer) => {
        // Жеребкування/Групи/Розклад match cards read this same player's photo
        // off useTournamentPlayerImages' own per-tournament cache, which has no
        // other way to learn about it - it only ever (re)fetches once per
        // tournament for the whole page session. Without this, a photo added
        // here stayed invisible there until a full page reload. See
        // useTournamentPlayerImages.js.
        const newPlayerId = newPlayer?.player_id || newPlayer?.id;
        if (newPlayerId) invalidateTournamentPlayerImage(tournament.id, newPlayerId, newPlayer?.image);

        if (tournament.grade === 1) {
            setPlayers(prev => {
                if (editingPlayerId) {
                    return prev.map(p =>
                        (p?.player_id === editingPlayerId || p?.id === editingPlayerId) ? newPlayer : p
                    );
                }
                const updated = [...prev];
                updated[editingSlot] = newPlayer;
                return updated;
            });
        } else {
            setPlayers(prev => {
                const updated = [...prev];

                // ЗАЩИТА: Находим индекс команды (чтобы огромное число не сломало массив)
                let teamIndex = editingSlot;
                if (teamIndex === null) {
                    teamIndex = prev.findIndex(t => t?.id === newPlayer.team_id);
                    if (teamIndex === -1) teamIndex = prev.length;
                }
                if (!updated[teamIndex]) {
                    updated[teamIndex] = {
                        players: [],
                    };
                }
                const team = updated[teamIndex];

                // Обязательно обновляем ID, если бэкенд вернул новый
                if (newPlayer.team_id) {
                    team.id = newPlayer.team_id;
                }
                // team_number (see getSafeTeamNumber) is only a random
                // collision-free key used to create/look up the team on the
                // backend - it is NOT the team's display number, so it must
                // never be stored as team.number. Leave team.number unset
                // here; the render fallback below (ordering-based index)
                // already matches what the backend assigns and shows
                // correctly once teams are refetched.

                const filteredPlayers = (team.players || []).filter(
                    p => p?.sorting !== newPlayer.sorting
                );
                team.players = [...filteredPlayers, newPlayer];
                return updated;
            });
        }
        setPlayerModalOpen(false);
        // No fetchPlayers() here - it would reset the whole (possibly
        // infinite-scroll-loaded) list back to page 1, throwing away
        // everything scrolled past and jumping the view back to the top.
        // The optimistic updates above already reflect the new state.
    };

    // Rating badges everywhere on this tournament (Участники list/grid,
    // Результати tab's own cards) open the same modal through this bus -
    // it lives in the always-mounted Header.js, so this never touches the
    // `tab=` query param and never triggers Tournament.js's tab switch
    // (which fully unmounts/remounts whichever tab you're on).
    const openRatingFor = (playerId) => openTournamentRating(playerId);

    if (loading || !tournament) return <PageLoader />;

    const playerSlots = Array.from({length: tournament.player_quantity});
    const slotSize = tournament.grade === 1 ? 1 : tournament.grade === 2 ? 2 : tournament.team_size;
    const canWrite = tournament?.can_write_in_base;

    // An empty-looking row inside a pair/team that ALREADY has slotSize
    // members means the roster doesn't line up with its own slots - the rows
    // are rendered by `find(p => p.sorting === i)`, so a member whose sorting
    // collides with another's (the live [0, 1, 1] pairs, see
    // src/utils/normalizeTeamPlayers.js) has no row of its own and leaves a
    // phantom "+ Гравець" behind. Adding there would create yet another
    // member instead of filling anything, so refuse and let the admin remove
    // the duplicate first (visible in Рейтинг, or via Поміняти гравця on the
    // row that does render).
    const canFillTeamSlot = (team) => {
        // slotSize is tournament.team_size for grade 3 and can still be unset
        // there - with no known slot count there's nothing to compare against,
        // so don't stand in the way of adding.
        if (!(slotSize > 0)) return true;
        if ((team?.players || []).filter(Boolean).length < slotSize) return true;
        toast.error(t("All player slots of this participant are already filled"));
        return false;
    };

    const getSafeTeamNumber = (slotIndex) => {
        if (slotIndex !== null && players[slotIndex]?.number) {
            return players[slotIndex].number;
        }
        // No confirmed existing team at this slot yet - do NOT fall back to
        // slotIndex + 1. The backend looks up/creates a team by this exact
        // number (Team.objects.get_or_create(tournament_id=..., number=...)),
        // so a guessed number that happens to collide with an unrelated
        // existing team silently attaches the new player to THAT team
        // instead of creating a fresh one for this slot. Use the same
        // effectively-collision-free random range already used below for the
        // "no slot" case.
        return Math.floor(Math.random() * 20000) + 10000;
    };

    const deletePlayerFromTournament = async (player, onSuccess) => {
        const playerId = player?.player_id || player?.id;
        if (!playerId) {
            console.error("No player ID to delete:", player);
            return;
        }
        try {
            await api.delete(`/api/v1.0/tournaments/${tournament.id}/players/${playerId}/`);
            toast.success(t("Player deleted successfully"));
            // onSuccess already removes the player from local state - no
            // fetchPlayers() here, it would reset the infinite-scroll list
            // back to page 1 and jump the view away from where you were.
            if (onSuccess) onSuccess();
        } catch (error) {
            if (error.response?.status === 500) {
                history.push('/500');
            } else if (error.response?.status === 403) {
                toast.error(t("The club administrator does not have access to delete"));
            } else {
                toast.error(t("Player already used in tournament groups or brackets"));
            }
        }
    };

    const openPlayerActions = (player, onChangePlayer, onDeleteSuccess) => {
        const fullName = `${player.first_name || ""} ${player.last_name || ""}`.trim();
        setActionsModal({
            title: fullName,
            actions: [
                { icon: "user", label: t("Profile"), onClick: () => handleItemClick(player, history, location) },
                ...(canWrite ? [{ icon: "edit-2", label: t("Edit profile"), onClick: () => { setEditingPlayer(player); setEditProfileOpen(true); } }] : []),
                { icon: "edit", label: t("Change player"), onClick: onChangePlayer },
                { icon: "trash-2", label: t("Delete"), danger: true, onClick: () => deletePlayerFromTournament(player, onDeleteSuccess) }
            ]
        });
    };

    const handleGridAction = async (type, data) => {
        if (type === 'edit_slot') {
            if (is_authorized) {
                if (data.player) {
                    const playerId = data.player?.player_id || data.player?.id;
                    openPlayerActions(data.player, () => {
                        setEditingSlot(data.index);
                        setEditingPlayerId(playerId || null);
                        setSelectedSorting(data.slot);
                        setPlayerModalOpen(true);
                    }, () => {
                        if (tournament.grade === 1) {
                            setPlayers(prev => prev.filter(p => (p?.player_id || p?.id) !== playerId));
                        } else {
                            setPlayers(prev => prev.map((teamIt, teamIndex) => {
                                if (teamIndex !== data.index) return teamIt;
                                return { ...teamIt, players: (teamIt.players || []).filter(p => p?.sorting !== data.slot) };
                            }));
                        }
                    });
                } else {
                    if (tournament.grade > 1 && !canFillTeamSlot(players[data.index])) return;
                    setEditingSlot(data.index);
                    setEditingPlayerId(null);
                    setSelectedSorting(data.slot);
                    setPlayerModalOpen(true);
                }
            } else if (data.player) {
                handleItemClick(data.player, history, location);
            }
        } else if (type === 'delete_team') {
            if (window.confirm(t("Are you sure you want to delete this team?"))) {
                try {
                    await api.delete(`/api/v1.0/tournaments/teams/${data.teamId}/`);
                    toast.success(t("Team deleted successfully"));

                    // No fetchPlayers() after this - it would reset the
                    // infinite-scroll list back to page 1 and jump the view
                    // away from where you were. The filter below already
                    // reflects the new state.
                    setPlayers(prev => prev.filter(p => p?.id !== data.teamId));
                } catch (e) {
                    if (e.response?.status === 400) {
                        toast.error(t("Team already used in tournament groups or brackets"));
                    } else {
                        toast.error(t("Failed to delete team"));
                    }
                    console.error(e);
                }
            }
        }
    };

    const renderSlotCard = (index, slotPlayers, matched) => {
        const slotStart = index * slotSize;
        const player = slotPlayers[0] || null;
        const sortingValue = slotStart;

        const isLastLoaded = players.length > 0 && index === Math.ceil(players.length / slotSize) - 1;

        const handleClick = () => {
            if (is_authorized) {
                setEditingSlot(index);
                setEditingPlayerId(player?.player_id || player?.id || null);
                setSelectedSorting(sortingValue);
                setPlayerModalOpen(true);
            } else if (player) {
                handleItemClick(player, history, location);
            }
        };

        // same entity-card look as the Tournaments/Clubs/Players list pages -
        // no header row, thumb + title/subtitle, everything else (city,
        // level, rating) folded into a meta line under the name
        return (
            <div
                className={`entity-card ${searchStateClass(matched)}`}
                key={index}
                onClick={() => {
                    if (player) {
                        if (is_authorized) {
                            const playerId = player?.player_id || player?.id;
                            openPlayerActions(player, handleClick, () =>
                                setPlayers(prev => prev.filter(p => (p?.player_id || p?.id) !== playerId))
                            );
                        } else {
                            handleItemClick(player, history, location);
                        }
                    } else if (is_authorized) {
                        handleClick();
                    }
                }}
                ref={isLastLoaded ? lastElementRef : null}
            >
                <div className="entity-card-header rating-card-header dashed-line">
                    <span className="entity-card-number">#{index + 1}</span>
                    {tournament?.rating && player?.rating != null && (
                        <span
                            className="entity-card-rating cursor"
                            onClick={(e) => { e.stopPropagation(); openRatingFor(player.player_id || player.id); }}
                        >
                            <FeatherIcon icon="bar-chart-2" size={12} />{player.rating}
                        </span>
                    )}
                </div>
                <div className="entity-card-top">
                    <div className="entity-card-thumb">
                        {loadingMore && index >= players.length ? <Skeleton circle width={52} height={52} /> : <SmoothAvatar src={player?.image} name={player ? `${player.first_name || ""} ${player.last_name || ""}`.trim() : ""} />}
                    </div>
                    <div className="entity-card-titles">
                        {player ? (
                            <>
                                <div className="entity-card-title">
                                    <span className="uppercase">{player.last_name}</span>
                                    {player.first_name && <>, {player.first_name}</>}
                                </div>
                                <div className="entity-card-subtitle">
                                    {player.city}
                                    {player.city && player.country && ", "}
                                    {player.country && t(getOptionName(COUNTRY_OPTIONS, player.country))}
                                    {(player.city || player.country) && player.level && ", "}
                                    {player.level && t(getOptionName(LEVEL_OPTIONS, player.level))}
                                </div>
                            </>
                        ) : (loadingMore && index >= players.length) ? <><Skeleton width={80} height={16} /><Skeleton width={80} height={16} /></> :
                            <div className="entity-card-title">
                                <span className={`cursor ${is_authorized ? "add-score" : ""}`}>{is_authorized && "+ "} {t("Player")}</span>
                            </div>}
                    </div>
                </div>
            </div>
        );
    };

    const renderSlotCardTeam = (team, index, matched) => {

        const teamPlayers = team?.players || [];
        const isLastLoaded = players.length > 0 && index === players.length - 1;

        const rows = Array.from({ length: slotSize }).map((_, i) =>
            teamPlayers.find(p => p?.sorting === i) || null
        );

        const totalRating =
            team?.team_rating ??
            rows.reduce((acc, p) => acc + (p?.rating || 0), 0);

        const hasPlayers = rows.some(p => p);

        const handleTeamRowClick = (player, i) => {
            if (player) {
                if (is_authorized) {
                    const playerId = player?.player_id || player?.id;
                    openPlayerActions(player, () => {
                        setEditingSlot(index);
                        setEditingPlayerId(playerId || null);
                        setSelectedSorting(i);
                        setPlayerModalOpen(true);
                    }, () => {
                        setPlayers(prev => prev.map((teamIt, teamIndex) => {
                            if (teamIndex !== index) return teamIt;
                            return { ...teamIt, players: (teamIt.players || []).filter(p => p?.sorting !== i) };
                        }));
                    });
                } else {
                    handleItemClick(player, history, location);
                }
            } else if (is_authorized) {
                if (!canFillTeamSlot(team)) return;
                setEditingSlot(index);
                setEditingPlayerId(null);
                setSelectedSorting(i);
                setPlayerModalOpen(true);
            }
        };

        return (
            <div className={`team-card ${searchStateClass(matched)}`} key={index} ref={isLastLoaded ? lastElementRef : null}>
                <div className="team-card-header rating-card-header dashed-line">
                    <div className="team-card-header-left">
                        <span className="team-card-number">
                            #{team?.number || (ordering === "-created_at" ? tournament.player_quantity - index : index + 1)}
                        </span>
                        {tournament.grade === 3 && (
                            is_authorized ? (
                                <input
                                    type="text"
                                    className="team-name-input"
                                    value={(team?.id ? teamNameDraft[team.id] : undefined) ?? team?.name ?? ""}
                                    onChange={(e) => {
                                        if (!team || !team.id) {
                                            toast.error(t("Please add at least one player to set team name"));
                                            return;
                                        }
                                        setTeamNameDraft(prev => ({
                                            ...prev,
                                            [team.id]: e.target.value
                                        }));
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder={t("Enter team name")}
                                />
                            ) : (
                                <span className="team-name-display">{team?.name ?? ""}</span>
                            )
                        )}
                    </div>
                    {((tournament?.rating && hasPlayers) || (is_authorized && team?.id)) && (
                        <div className="team-card-header-right">
                            {tournament?.rating && hasPlayers && (
                                <span className="team-card-rating">
                                    <FeatherIcon icon="bar-chart-2" size={12} />{totalRating}
                                </span>
                            )}
                            {is_authorized && team?.id && (
                                <span
                                    className="delete-team"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleGridAction('delete_team', { teamId: team.id });
                                    }}
                                >
                                    <FeatherIcon icon="x" size={14} />
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {rows.map((player, i) => (
                    <div
                        className="team-player-row-full"
                        key={i}
                        onClick={() => handleTeamRowClick(player, i)}
                    >
                        <div className="team-player-avatar">
                            {loadingMore && index >= players.length && !player ? <Skeleton circle width={44} height={44} /> : <SmoothAvatar src={player?.image} name={player ? `${player.first_name || ""} ${player.last_name || ""}`.trim() : ""} />}
                        </div>
                        <div className="team-player-info">
                            {player ? (
                                <>
                                    <span className="black-name">
                                        <span className="uppercase">{player.last_name}</span>
                                        {player.first_name && <>, {player.first_name}</>}
                                    </span>
                                    <span className="gray">
                                        {player.city}
                                        {player.city && player.country && ", "}
                                        {player.country && t(getOptionName(COUNTRY_OPTIONS, player.country))}
                                        {(player.city || player.country) && player.level && ", "}
                                        {player.level && t(getOptionName(LEVEL_OPTIONS, player.level))}
                                    </span>
                                </>
                            ) : (loadingMore && index >= players.length) ? <><Skeleton width={80} height={16} /><Skeleton width={60} height={16} /></>
                                : is_authorized ? <span className="cursor add-score">+ {t("Player")}</span> : t("Player")}
                        </div>
                        {tournament?.rating && player?.rating != null && (
                            <span
                                className="player-edge-rating cursor"
                                onClick={(e) => { e.stopPropagation(); openRatingFor(player.player_id || player.id); }}
                            >
                                <FeatherIcon icon="bar-chart-2" size={12} />{player.rating}
                            </span>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    // Compact Table Mode - singles (grade 1). Same slot/click semantics as
    // renderSlotCard above, just CompactTableRow markup instead of
    // .entity-card. See frontend-tasks/2026-09-02-compact-table-mode.md.
    const renderCompactSlotRow = (index, slotPlayers, zebra, matched) => {
        const slotStart = index * slotSize;
        const player = slotPlayers[0] || null;
        const sortingValue = slotStart;
        const isLastLoaded = players.length > 0 && index === Math.ceil(players.length / slotSize) - 1;
        const rowRef = isLastLoaded ? lastElementRef : null;

        if (loadingMore && index >= players.length) {
            // Same wrapper spans (trow-index/-avatar/-name-text/-actions) as
            // CompactTableRow's own real-row markup below - bare/unwrapped
            // placeholders here previously gave the grid row a different
            // height/padding than the actual rows around it.
            return (
                <div className="trow trow-tournament-players" key={index} ref={rowRef} style={{ pointerEvents: "none" }}>
                    <span className="trow-index" />
                    <span className="trow-avatar"><Skeleton circle width={20} height={20} /></span>
                    <span className="trow-name-text"><Skeleton width="60%" height={12} /></span>
                    <span className="trow-actions" />
                </div>
            );
        }

        const handleClick = () => {
            if (is_authorized) {
                setEditingSlot(index);
                setEditingPlayerId(player?.player_id || player?.id || null);
                setSelectedSorting(sortingValue);
                setPlayerModalOpen(true);
            } else if (player) {
                handleItemClick(player, history, location);
            }
        };

        const subtitle = player ? [
            player.city,
            player.country && t(getOptionName(COUNTRY_OPTIONS, player.country)),
            player.level && t(getOptionName(LEVEL_OPTIONS, player.level))
        ].filter(Boolean).join(", ") : undefined;

        return (
            <CompactTableRow
                key={index}
                ref={rowRef}
                variant="tournament-players"
                zebra={zebra}
                className={searchStateClass(matched)}
                indexLabel={`#${index + 1}`}
                avatarSrc={player?.image}
                title={
                    player
                        ? <>{player.last_name}{player.first_name && <>, {player.first_name}</>}</>
                        : <span className={`cursor ${is_authorized ? "add-score" : ""}`}>{is_authorized && "+ "}{t("Player")}</span>
                }
                subtitle={subtitle}
                actions={tournament?.rating && player?.rating != null && (
                    <span className="row-stat cursor" onClick={() => openRatingFor(player.player_id || player.id)}>
                        <FeatherIcon icon="bar-chart-2" />{player.rating}
                    </span>
                )}
                onClick={() => {
                    if (player) {
                        if (is_authorized) {
                            const playerId = player?.player_id || player?.id;
                            openPlayerActions(player, handleClick, () =>
                                setPlayers(prev => prev.filter(p => (p?.player_id || p?.id) !== playerId))
                            );
                        } else {
                            handleItemClick(player, history, location);
                        }
                    } else if (is_authorized) {
                        handleClick();
                    }
                }}
            />
        );
    };

    // Compact Table Mode - pairs/teams (grade > 1). A team doesn't fit the
    // one-row-per-entity model every other Compact Table variant uses (it's
    // itself a group of slotSize player rows), so it's bespoke markup
    // (.compact-team/-header/-players) rather than CompactTableRow, reusing
    // just the shared avatar/name/stat classes for the nested player rows.
    const renderCompactTeam = (team, index, zebra, matched) => {
        const isLastLoaded = players.length > 0 && index === players.length - 1;
        const rowRef = isLastLoaded ? lastElementRef : null;

        if (loadingMore && index >= players.length && !team) {
            // Mirrors whichever real shape this grade renders below - a
            // bare 40%-wide bar in a plain .compact-team div (grade 3's own
            // header+roster shape) read as broken/way too short once pairs
            // switched to the one-line .compact-pair-row layout, which has
            // its own court-badge circle to match.
            if (tournament.grade !== 3) {
                return (
                    <div className={`compact-team compact-pair-row${zebra ? " trow-odd" : ""}`} key={index} ref={rowRef} style={{ pointerEvents: "none" }}>
                        <Skeleton circle width={26} height={26} />
                        <span className="compact-pair-names"><Skeleton width="70%" height={12} /></span>
                    </div>
                );
            }
            return (
                <div className={`compact-team${zebra ? " trow-odd" : ""}`} key={index} ref={rowRef} style={{ pointerEvents: "none" }}>
                    <Skeleton width="40%" height={12} />
                </div>
            );
        }

        const teamPlayers = team?.players || [];
        const rows = Array.from({ length: slotSize }).map((_, i) =>
            teamPlayers.find(p => p?.sorting === i) || null
        );

        const totalRating =
            team?.team_rating ??
            rows.reduce((acc, p) => acc + (p?.rating || 0), 0);

        const hasPlayers = rows.some(p => p);

        const handleTeamRowClick = (player, i) => {
            if (player) {
                if (is_authorized) {
                    const playerId = player?.player_id || player?.id;
                    openPlayerActions(player, () => {
                        setEditingSlot(index);
                        setEditingPlayerId(playerId || null);
                        setSelectedSorting(i);
                        setPlayerModalOpen(true);
                    }, () => {
                        setPlayers(prev => prev.map((teamIt, teamIndex) => {
                            if (teamIndex !== index) return teamIt;
                            return { ...teamIt, players: (teamIt.players || []).filter(p => p?.sorting !== i) };
                        }));
                    });
                } else {
                    handleItemClick(player, history, location);
                }
            } else if (is_authorized) {
                if (!canFillTeamSlot(team)) return;
                setEditingSlot(index);
                setEditingPlayerId(null);
                setSelectedSorting(i);
                setPlayerModalOpen(true);
            }
        };

        const number = team?.number || (ordering === "-created_at" ? tournament.player_quantity - index : index + 1);

        // Pairs (grade 2, no team name of their own) - one Розклад-style
        // agenda row instead of a header + two avatar rows: a round number
        // chip (same .schedule-agenda-court look as the court badge there),
        // both names on one line ("Іщенко, В. / Амбарнікова, Л."), total
        // rating at the end. Each name keeps its own click target (add/
        // edit/delete via handleTeamRowClick below), same as the old
        // per-player rows did - just inline instead of stacked.
        if (tournament.grade !== 3) {
            return (
                <div className={`compact-team compact-pair-row${zebra ? " trow-odd" : ""} ${searchStateClass(matched)}`} key={index} ref={rowRef}>
                    <span className="schedule-agenda-court">{number}</span>
                    <span className="compact-pair-names">
                        {rows.map((player, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && <span className="compact-pair-sep">/</span>}
                                <span className="cursor" onClick={() => handleTeamRowClick(player, i)}>
                                    {player
                                        ? <>{player.last_name}{player.first_name && <>, {player.first_name}</>}</>
                                        : <span className={is_authorized ? "add-score" : ""}>{is_authorized ? `+ ${t("Player")}` : t("Player")}</span>
                                    }
                                </span>
                            </React.Fragment>
                        ))}
                    </span>
                    {tournament?.rating && hasPlayers && (
                        <span className="row-stat">
                            <FeatherIcon icon="bar-chart-2" />{totalRating}
                        </span>
                    )}
                    {is_authorized && team?.id && (
                        <span
                            className="compact-team-delete"
                            onClick={() => handleGridAction('delete_team', { teamId: team.id })}
                        >
                            <FeatherIcon icon="x" size={14} />
                        </span>
                    )}
                </div>
            );
        }

        return (
            <div className={`compact-team${zebra ? " trow-odd" : ""} ${searchStateClass(matched)}`} key={index} ref={rowRef}>
                <div className="compact-team-header">
                    <span className="compact-team-header-left">
                        <span className="compact-team-number">
                            #{number}
                        </span>
                        {is_authorized ? (
                            <input
                                type="text"
                                className="compact-team-name-input"
                                value={(team?.id ? teamNameDraft[team.id] : undefined) ?? team?.name ?? ""}
                                onChange={(e) => {
                                    if (!team || !team.id) {
                                        toast.error(t("Please add at least one player to set team name"));
                                        return;
                                    }
                                    setTeamNameDraft(prev => ({ ...prev, [team.id]: e.target.value }));
                                }}
                                placeholder={t("Enter team name")}
                            />
                        ) : (
                            <span className="compact-team-name">{team?.name ?? ""}</span>
                        )}
                    </span>
                    <span className="compact-team-header-right">
                        {tournament?.rating && hasPlayers && (
                            <span className="row-stat">
                                <FeatherIcon icon="bar-chart-2" />{totalRating}
                            </span>
                        )}
                        {is_authorized && team?.id && (
                            <span
                                className="compact-team-delete"
                                onClick={() => handleGridAction('delete_team', { teamId: team.id })}
                            >
                                <FeatherIcon icon="x" size={14} />
                            </span>
                        )}
                    </span>
                </div>
                <div className="compact-team-players">
                    {rows.map((player, i) => {
                        const isSkeleton = loadingMore && index >= players.length && !player;
                        return (
                            <div
                                className="compact-team-player"
                                key={i}
                                onClick={() => handleTeamRowClick(player, i)}
                            >
                                <span className="trow-avatar">
                                    {isSkeleton
                                        ? <Skeleton circle width={20} height={20} />
                                        : player?.image && <img src={player.image} alt="" />}
                                </span>
                                <span className="trow-name-text">
                                    {player ? (
                                        <>
                                            {player.last_name}
                                            {player.first_name && <>, {player.first_name}</>}
                                            {(player.city || player.country || player.level) && (
                                                <span className="trow-subtitle"> · {[
                                                    player.city,
                                                    player.country && t(getOptionName(COUNTRY_OPTIONS, player.country)),
                                                    player.level && t(getOptionName(LEVEL_OPTIONS, player.level))
                                                ].filter(Boolean).join(", ")}</span>
                                            )}
                                        </>
                                    ) : isSkeleton ? <Skeleton width={80} height={12} /> : (
                                        <span className={`cursor ${is_authorized ? "add-score" : ""}`}>{is_authorized && "+ "}{t("Player")}</span>
                                    )}
                                </span>
                                {tournament?.rating && player?.rating != null && (
                                    <span
                                        className="row-stat cursor"
                                        onClick={(e) => { e.stopPropagation(); openRatingFor(player.player_id || player.id); }}
                                    >
                                        <FeatherIcon icon="bar-chart-2" />{player.rating}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <>
            {/* Результати (this tournament's own place/score/stats view)
                moved to the top-level Rating tab's own "Очки турніру" sub-
                tab (TournamentRatingTab.js) - only "List" is left here, kept
                (not collapsed away) so this bar still matches the same
                stage-tabs-wrap look every other tab uses. */}
            <ul className="tabs" style={{ marginTop: "10px", marginBottom: "10px" }}>
                <li className="stage-tabs-wrap">
                    <ul className="stage-tabs-scroll">
                        <li className="active">
                            <a href="#" onClick={(e) => e.preventDefault()}>{t("List")}</a>
                        </li>
                    </ul>
                </li>
                {tournament?.rating && (
                    <li className="right">
                        <a href="#" className="action-button matches-btn" onClick={(e) => { e.preventDefault(); openTournamentRating(); }}>
                            <FeatherIcon icon="bar-chart-2" />
                            <span className="btn-label">{t("Rating")}</span>
                        </a>
                    </li>
                )}
                {!!tournament?.player_quantity && !compactTable && (
                    <IconDropdown
                        activeId={viewMode}
                        onChange={toggleViewMode}
                        className="action-button matches-btn"
                        options={[
                            { id: "list", icon: "menu", label: "List" },
                            { id: "grid", icon: "grid", label: "Grid" }
                        ]}
                    />
                )}
            </ul>
            <>
            <div>
                {playersLoading
                    ? (
                        <PageLoader />
                    )
                    : !tournament?.player_quantity ? (
                        <EmptyData
                            title="No players settings configured"
                            text="Players settings have not been configured yet."
                            actionLabel="Go to settings"
                            onAction={is_authorized ? () => openTournamentSettings("players") : undefined}
                        />
                    )
                    : effectiveViewMode === "grid" ? (
                        <div id="participants-block" style={{ width: '100%' }}>
                            <PlayersGridView
                                players={players}
                                tournament={tournament}
                                is_authorized={is_authorized}
                                onAction={handleGridAction}
                                lastElementRef={lastElementRef}
                                searchChips={searchChips}
                                teamNameDraft={teamNameDraft}
                                setTeamNameDraft={setTeamNameDraft}
                                onOpenRating={openRatingFor}
                                ordering={ordering}
                                t={t}
                            />
                        </div>
                    ) : tournament.grade > 1
                        ? <div className={compactTable ? "" : "admin-wrapper"} id="participants-block">
                            <div className={compactTable ? "compact-teams" : `team-table tournament-players-table ${!tournament?.rating ? "no-rating" : ""} ${!is_authorized ? "unauthorized small_view" : tournament?.can_edit ? "" : "small_view"}`}>
                                {
                                    (() => {
                                        let compactRowIndex = 0;
                                        return Array.from({ length: tournament.player_quantity }).map((_, index) => {
                                            const team = players[index] || null;
                                            const matched = matchOf(team);

                                            return compactTable
                                                ? renderCompactTeam(team, index, compactRowIndex++ % 2 === 1, matched)
                                                : renderSlotCardTeam(team, index, matched);
                                        });
                                    })()
                                }
                                {loadingMore && <div style={{ textAlign: "center", padding: "10px", color: "var(--secondary-color)" }}>{t("Loading...")}</div>}
                            </div>
                        </div>
                        : <div id="participants-block">
                            <div className={compactTable ? "trows" : "entity-cards"}>
                            {
                                (() => {
                                    let compactRowIndex = 0;
                                    return playerSlots.map((_, index) => {
                                        const slotStart = index * slotSize;
                                        const slotPlayers = players.slice(slotStart, slotStart + slotSize);
                                        const matched = matchOf(slotPlayers[0]);

                                        return compactTable
                                            ? renderCompactSlotRow(index, slotPlayers, compactRowIndex++ % 2 === 1, matched)
                                            : renderSlotCard(index, slotPlayers, matched);
                                    });
                                })()
                            }
                            {loadingMore && <div style={{ textAlign: "center", padding: "10px", color: "var(--secondary-color)" }}>{t("Loading...")}</div>}
                            </div>
                        </div>
                }
            </div>
            </>

            <CustomModal isOpen={playerModalOpen} onClose={() => setPlayerModalOpen(false)} centered>
                <TournamentPlayerForm
                    tournamentId={tournament.id}
                    playerBase={tournament.player_base}
                    rating={tournament.rating}
                    sorting={selectedSorting}
                    oldPlayerId={editingPlayerId}
                    teamNumber={tournament.grade > 1 ? getSafeTeamNumber(editingSlot) : undefined}
                    teamId={tournament.grade === 1 ? undefined : (editingSlot !== null ? players[editingSlot]?.id : undefined)}
                    onClose={() => setPlayerModalOpen(false)}
                    onSuccess={handlePlayerAdded}
                    canWrite={canWrite}
                />
            </CustomModal>

            <CustomModal
                isOpen={editProfileOpen}
                onClose={() => setEditProfileOpen(false)}
            >
                <PlayerForm
                    baseId={tournament.player_base}
                    playerId={editingPlayer?.player_id || editingPlayer?.id || null}

                    onClose={() => setEditProfileOpen(false)}

                    onSuccess={(updatedPlayer) => {
                        const isEdited = (p) =>
                            p?.player_id === updatedPlayer.id || p?.id === updatedPlayer.id;

                        // Same reasoning as handlePlayerAdded above - a photo
                        // edited here needs to reach useTournamentPlayerImages'
                        // own cache, or Жеребкування/Групи/Розклад keep
                        // showing whatever (or nothing) that player had before.
                        if (updatedPlayer?.id) {
                            invalidateTournamentPlayerImage(tournament.id, updatedPlayer.id, updatedPlayer.image);
                        }

                        if (tournament.grade === 1) {
                            setPlayers(prev => prev.map(p => (isEdited(p) ? mergePlayerProfile(p, updatedPlayer) : p)));
                        }

                        else {
                            setPlayers(prev =>
                                prev.map(team => ({
                                    ...team,
                                    players: team.players?.map(p => (isEdited(p) ? mergePlayerProfile(p, updatedPlayer) : p))
                                }))
                            );
                        }

                        setEditProfileOpen(false);
                        // Deliberately NO fetchPlayers() here - same reasoning as the
                        // other mutation handlers in this file: it flips playersLoading
                        // (whole list blinks to skeletons) and setPlayers(first page)
                        // throws away everything infinite-scroll had appended, bouncing
                        // you back to the top. Editing one player's photo after
                        // scrolling to #80 sent you back to #20, which made adding
                        // photos to many players in a row unusable. The targeted merge
                        // above already refreshes every field the row actually renders
                        // (image/first_name/last_name/city/level).
                    }}

                    img={editingPlayer?.image || null}
                    width={editingPlayer?.width || null}
                    height={editingPlayer?.height || null}
                    canWrite={true}
                    hideDelete={true}
                />
            </CustomModal>

            <ParticipantActionsModal
                isOpen={!!actionsModal}
                onClose={() => setActionsModal(null)}
                title={actionsModal?.title}
                actions={actionsModal?.actions || []}
            />
        </>
    );
};

export default TournamentPlayers;
